/** @jsx jsx */
import React, { useEffect } from 'react';
import { css, styled, jsx } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Image from '@atlaskit/image';
import { Box, Stack, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import {
	UNSAFE_DO_NOT_USE_BANNER_HEIGHT,
	UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT,
} from '@atlassian/jira-navigation-system/src/index.tsx';
import type { preview_projectsDirectoryTemplatesSidebar_TemplatePreview$key } from '@atlassian/jira-relay/src/__generated__/preview_projectsDirectoryTemplatesSidebar_TemplatePreview.graphql';

import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_REFRESH } from '../../constants.tsx';

interface Props {
	update?: () => Promise<{} | null>;
	previewRef: preview_projectsDirectoryTemplatesSidebar_TemplatePreview$key;
	refreshEnabled: boolean;
}

const usePreviewWidth = () => {
	const leftSidebarState = useLeftSidebarState();
	const leftSidebarWidth = leftSidebarState.isCollapsed ? 0 : leftSidebarState.width;

	// left side nav gets automatically closed at 64rem and leftSidebarState no longer accurately reflects the state
	const isMobileView =
		globalThis.window && !globalThis.window.matchMedia('(min-width: 64rem)').matches;

	const leftSidebarHidden = isMobileView;

	// Temporary workaround to panel bug that enforces 365px width below 48rem
	const sidebarWidth =
		globalThis.window && globalThis.window.matchMedia('(min-width: 48rem)').matches
			? SIDEBAR_WIDTH_REFRESH
			: 365;

	return (
		(globalThis.window?.innerWidth || 0) - sidebarWidth - (leftSidebarHidden ? 0 : leftSidebarWidth)
	);
};

export const TemplatePreview = ({ update, previewRef, refreshEnabled }: Props) => {
	const { title, description, key, previewUrl, previewDarkUrl } =
		useFragment<preview_projectsDirectoryTemplatesSidebar_TemplatePreview$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment preview_projectsDirectoryTemplatesSidebar_TemplatePreview on JiraProjectListViewTemplateItem {
					title @required(action: THROW)
					description @required(action: THROW)
					key @required(action: THROW)
					previewUrl @required(action: THROW)
					previewDarkUrl @required(action: THROW)
				}
			`,
			previewRef,
		);

	// Required to update the Popup location when the trigger has moved during side nav animation
	// eslint-disable-next-line react-you-might-not-need-an-effect/no-manage-parent
	useEffect(() => {
		if (update) update();
	}, [update]);

	const previewWidth = usePreviewWidth();
	if (previewWidth < 300) return null;

	return (
		<Box
			xcss={[TemplatePreviewWrapperStyles, !refreshEnabled && WidthStyles]}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				width: refreshEnabled ? previewWidth : undefined,
			}}
		>
			{refreshEnabled ? (
				<span css={templateAreaContainerVariationStyles}>
					<Stack xcss={PreviewWrapperStyles} space="space.300">
						<Stack space="space.100" xcss={TextContainerStylesVariation}>
							<Heading size="large" as="span">
								{title}
							</Heading>
							<Text size="medium" as="p">
								{description}
							</Text>
						</Stack>
						<Box as="span" xcss={ImageWrapperStylesVariation}>
							<PreviewImage src={previewUrl} key={key} alt="" srcDark={previewDarkUrl} />
						</Box>
					</Stack>
				</span>
			) : (
				<TemplateAreaContainer>
					<Stack xcss={PreviewWrapperStyles} space="space.500">
						<Stack space="space.100" xcss={TextContainerStyles}>
							<Box as="span" xcss={TitleStyles}>
								{title}
							</Box>
							<Box as="p" xcss={DescriptionStyles}>
								{description}
							</Box>
						</Stack>
						<Box as="span" xcss={ImageWrapperStyles}>
							<PreviewImage src={previewUrl} key={key} alt="" srcDark={previewDarkUrl} />
						</Box>
					</Stack>
				</TemplateAreaContainer>
			)}
		</Box>
	);
};

// GRW-1532 START

const templateAreaContainerVariationStyles = css({
	width: '75%',
	maxWidth: '436px',
});

const TextContainerStylesVariation = xcss({
	color: 'color.text',
	textAlign: 'center',
});

const ImageWrapperStylesVariation = xcss({
	aspectRatio: '436/360',
	maxHeight: '360px',
});

// GRW-1532 END

const TemplatePreviewWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT} - ${UNSAFE_DO_NOT_USE_BANNER_HEIGHT} - 4px)`,
	backgroundColor: 'elevation.surface',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	pointerEvents: 'none',
	':empty': {
		display: 'none',
	},
});

const WidthStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TemplateAreaContainer = styled.span({
	width: '75%',
	'@media (min-aspect-ratio: 11 / 6)': { maxWidth: '50%' },
});

const ImageWrapperStyles = xcss({
	aspectRatio: '100/62',
});

const TextContainerStyles = xcss({
	color: 'color.text',
});

const TitleStyles = xcss({
	font: token('font.heading.xlarge'),
	fontWeight: token('font.weight.regular'),
});

const DescriptionStyles = xcss({
	font: token('font.body'),
	margin: '0',
});

const PreviewWrapperStyles = xcss({
	backgroundColor: 'elevation.surface',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreviewImage = styled(Image)({
	objectFit: 'contain',
	objectPosition: 'top',
	width: '100%',
});
